<template>
  <div class="max-w-4xl mx-auto px-4 py-8 dark:bg-gray-900 dark:text-gray-100">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-3xl font-bold text-gray-800 dark:text-gray-100">My Performance</h2>
      <router-link
        to="/"
        class="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md text-sm dark:bg-blue-500 dark:hover:bg-blue-600"
      >
        Return to Home
      </router-link>
    </div>
    <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">Showing last 5 attempts per exam.</p>

    <div v-if="authLoading || loadingData" class="text-center py-12">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 dark:border-blue-400 border-t-transparent"></div>
      <p class="mt-2 text-gray-600 dark:text-gray-400">Loading performance data...</p>
    </div>

    <div v-else-if="!currentUser">
      <p class="text-center text-lg text-gray-700 dark:text-gray-300">
        Please <router-link to="/signup" class="text-blue-600 dark:text-blue-400 hover:underline">login</router-link> to view your performance.
      </p>
    </div>

    <div v-else>
      <div v-if="Object.keys(groupedAttempts).length > 0" class="space-y-8">
        <div v-for="(attempts, examId) in groupedAttempts" :key="examId" class="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6">
          <h3 class="text-2xl font-semibold text-gray-700 dark:text-gray-200 mb-3">
            {{ attempts[0]?.examName || examId }}
            <span class="text-base font-normal text-gray-500 dark:text-gray-400">({{ attempts[0]?.subject }} - {{ attempts[0]?.year }})</span>
          </h3>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead class="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Score</th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Percentage</th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Duration</th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                <tr v-for="attempt in attempts" :key="attempt.id">
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{{ new Date(attempt.date).toLocaleDateString() }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{{ attempt.score }}/{{ attempt.totalQuestions }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm"
                      :class="{
                        'text-green-600 dark:text-green-400': attempt.scorePercentage >= 80,
                        'text-yellow-600 dark:text-yellow-400': attempt.scorePercentage >= 60 && attempt.scorePercentage < 80,
                        'text-red-600 dark:text-red-400': attempt.scorePercentage < 60,
                      }">
                    {{ attempt.scorePercentage }}%
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{{ formatDuration(attempt.completedDuration) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-10 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path vector-effect="non-scaling-stroke" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2zm3-8V3M12 3v2m3-2v2" />
        </svg>
        <h3 class="mt-2 text-lg font-medium text-gray-900 dark:text-gray-100">No exam attempts yet</h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Come back here after you have attempted a few exams to track your progress.
        </p>
        <div class="mt-6">
          <router-link to="/" class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            Find an Exam
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'; // Added watch
import { useAuth } from '../composables/useAuth';
import { db } from '../firebaseConfig'; // Uncomment and use Firestore
import { collection, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore'; // Uncomment and use Firestore functions

const { currentUser, loading: authLoading } = useAuth();
const groupedAttempts = ref({}); // Changed from examAttempts = ref([])
const loadingData = ref(true); // Start true as we fetch on mount/watch

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return 'N/A';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

const fetchExamAttempts = async () => {
  if (!currentUser.value) {
    groupedAttempts.value = {};
    loadingData.value = false;
    return;
  }
  loadingData.value = true;
  console.log("Fetching all attempts for userId:", currentUser.value.uid);

  try {
    const userAttempts = {};
    // Step 1: Get all attempts for the user to find unique examSetIds
    const allAttemptsQuery = query(
      collection(db, 'examAttempts'),
      where("userId", "==", currentUser.value.uid),
      orderBy('date', 'desc') // Order by date to easily get unique examSetIds if needed, though not strictly necessary for this step
    );
    const allDocsSnapshot = await getDocs(allAttemptsQuery);
    const uniqueExamSetIds = new Set();
    allDocsSnapshot.docs.forEach(doc => {
      uniqueExamSetIds.add(doc.data().examSetId);
    });

    console.log("Unique examSetIds found:", Array.from(uniqueExamSetIds));

    // Step 2: For each unique examSetId, fetch the last 5 attempts
    for (const examId of uniqueExamSetIds) {
      console.log(`Fetching last 5 attempts for examId: ${examId} and userId: ${currentUser.value.uid}`);
      const specificExamQuery = query(
        collection(db, 'examAttempts'),
        where("userId", "==", currentUser.value.uid),
        where("examSetId", "==", examId),
        orderBy('date', 'desc'),
        limit(5)
      );
      const specificExamSnapshot = await getDocs(specificExamQuery);
      userAttempts[examId] = specificExamSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          date: data.date instanceof Timestamp ? data.date.toDate() : new Date(data.date)
        };
      });
      console.log(`Fetched ${userAttempts[examId].length} attempts for examId: ${examId}`);
    }
    groupedAttempts.value = userAttempts;
    console.log("Grouped attempts:", groupedAttempts.value);

  } catch (error) {
    console.error("Error fetching exam attempts:", error);
    groupedAttempts.value = {}; // Clear attempts on error
  } finally {
    loadingData.value = false;
  }
};

onMounted(() => {
  if (currentUser.value) {
    fetchExamAttempts();
  } else {
    loadingData.value = false; // Not loading if no user
  }
});

watch(currentUser, (newUser, oldUser) => {
  if (newUser && (!oldUser || newUser.uid !== oldUser.uid)) {
    fetchExamAttempts();
  } else if (!newUser) {
    groupedAttempts.value = {};
    loadingData.value = false;
  }
}, { immediate: false });

</script>

<style scoped>
/* Add any component-specific styles here if needed */
</style>